import React, { useEffect } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import { useSupportAuth } from "../context/SupportAuthContext";
import SessionTimer from "./SessionTimer";
import { SUPPORT_ACCESS_MODULES } from "../pages/settings/Employees";
import { fetchLeads } from "../store/slices/leadsSlice";
import { fetchGroups } from "../store/slices/groupSlice";
import { fetchSources } from "../store/slices/sourceSlice";
import { fetchStatus } from "../store/slices/statusSlice";
import { fetchEmployees } from "../store/slices/employeeSlice";
import {
  setFilteredReports,
  setNewfilteredReports,
  setFollowupReports,
} from "../store/slices/filteredDataSlice";

const SupportLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { leads } = useSelector((state) => state.leads);
  const { supportEmail, hasModuleAccess, requestMoreTime, logoutSupport } = useSupportAuth();
  const visibleModuleGroups = SUPPORT_ACCESS_MODULES.map((group) => ({
    ...group,
    subModules: (group.subModules || []).filter((sub) => hasModuleAccess(sub.mappedFeature)),
  })).filter((group) => group.subModules.length > 0);

  // Support users have no per-user filters of their own. Mirror what
  // DashFilters does in the regular dashboard: load reference data and
  // seed the filteredData slices with all leads so dashboard widgets
  // (Lead Source, Potential Status, Follow-up Table, Lead Conversion
  // Rate, Historical Data, Revenue Distribution, etc.) render the same
  // dataset that an L0 admin sees.
  useEffect(() => {
    dispatch(fetchLeads());
    dispatch(fetchGroups());
    dispatch(fetchSources());
    dispatch(fetchStatus());
    dispatch(fetchEmployees());
  }, [dispatch, location.pathname]);

  // Several dashboard widgets call `dispatch(setFilteredReports())` on
  // their own mount, which wipes the slice. Re-seed on every route
  // change as well as whenever leads change. React runs parent effects
  // after child effects, so this seed always runs *after* the widget's
  // clear, keeping data populated on every revisit.
  useEffect(() => {
    const safeLeads = Array.isArray(leads) ? leads : [];
    dispatch(setFilteredReports(safeLeads));
    dispatch(setNewfilteredReports(safeLeads));
    dispatch(setFollowupReports(safeLeads));
  }, [dispatch, leads, location.pathname]);

  const handleRequestMoreTime = async () => {
    try {
      await requestMoreTime();
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Failed to request more time.", {
        variant: "error",
      });
    }
  };

  return (
    <div className="w-screen h-screen flex bg-slate-100">
      <aside className="w-[16rem] bg-white border-r px-3 py-4 h-screen overflow-y-auto">
        <h2 className="text-lg font-semibold mb-1">Support</h2>
        <p className="text-xs text-gray-500 mb-4 break-all">{supportEmail || "support user"}</p>

        <nav className="space-y-1 pb-4">
          <NavLink
            to="/support/dashboard"
            className={({ isActive }) =>
              `block px-3 py-2 rounded text-sm ${
                isActive ? "bg-blue-600 text-white" : "hover:bg-slate-100 text-slate-700"
              }`
            }
          >
            Dashboard
          </NavLink>
          {visibleModuleGroups.map((group) => (
            <div key={group.id} className="pt-2">
              <p className="px-2 py-1 text-[11px] uppercase tracking-wide text-gray-500">{group.name}</p>
              {group.subModules.map((item) => (
                <NavLink
                  key={item.route}
                  to={`/support/dashboard/${item.route}`}
                  className={({ isActive }) =>
                    `block px-3 py-2 rounded text-sm ${
                      isActive ? "bg-blue-600 text-white" : "hover:bg-slate-100 text-slate-700"
                    }`
                  }
                >
                  {item.name}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      <main className="flex-1 p-4 overflow-auto">
        <div className="bg-white border rounded-md px-4 py-3 mb-4 flex items-center justify-between">
          <h1 className="font-semibold">Support Dashboard</h1>
          <div className="flex items-center gap-2">
            <SessionTimer />
            <button
              onClick={handleRequestMoreTime}
              className="px-3 py-2 rounded border border-blue-600 text-blue-700 text-sm"
            >
              Request More Time
            </button>
            <button
              onClick={() => {
                logoutSupport("");
                navigate("/support/login", { replace: true });
              }}
              className="px-3 py-2 rounded border border-red-500 text-red-600 text-sm"
            >
              Logout
            </button>
          </div>
        </div>
        <Outlet />
      </main>
    </div>
  );
};

export default SupportLayout;